import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { getMachineIdFromRoute } from 'app/services/utils.service';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import * as attachmentsActions from 'app/actions/attachments';
import { AttachmentsService } from 'app/services/attachment.service';
import { MediaService } from 'app/services/media/media.service';

@Injectable()
export class AttachmentsEffects {
  private actions$ = inject(Actions);
  private activatedRoute = inject(ActivatedRoute);
  private attachmentsService = inject(AttachmentsService);
  private mediaService = inject(MediaService);

  loadAttachments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(attachmentsActions.loadAttachments),
      switchMap(() => {
        const machineId = getMachineIdFromRoute(this.activatedRoute);
        return this.attachmentsService.getAttachments(machineId).pipe(
          map(attachments => attachmentsActions.loadAttachmentsSuccess({ attachments })),
          catchError(error => of(attachmentsActions.attachmentActionFail({ error })))
        );
      })
    );
  });

  addAttachments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(attachmentsActions.addAttachment),
      mergeMap(action => {
        const machineId = getMachineIdFromRoute(this.activatedRoute);
        const attachment = action.attachment;
        return this.mediaService.addAttachmentToMachine(machineId, attachment.path, attachment.file).pipe(
          map(() => attachmentsActions.loadAttachments()),
          catchError(error => of(attachmentsActions.attachmentActionFail({ error })))
        );
      })
    );
  });

  updateAttachments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(attachmentsActions.updateAttachment),
      switchMap(action => {
        const machineId = getMachineIdFromRoute(this.activatedRoute);
        return this.mediaService
          .updateAttachmentsOfMachine(machineId, {
            updates: action.attachments.map(a => ({ attachmentId: a.id, path: a.path }))
          })
          .pipe(
            map(() => attachmentsActions.loadAttachments()),
            catchError(error => of(attachmentsActions.attachmentActionFail({ error })))
          );
      })
    );
  });

  deleteAttachments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(attachmentsActions.deleteAttachment),
      switchMap(action => {
        const machineId = getMachineIdFromRoute(this.activatedRoute);
        return this.mediaService.deleteAttachmentsFromMachine(machineId, { mediaIds: action.attachmentIds }).pipe(
          map(() => attachmentsActions.loadAttachments()),
          catchError(err => of(attachmentsActions.attachmentActionFail(err)))
        );
      })
    );
  });

}
